import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Package, Image as ImageIcon, Mail, ScrollText,
  ShieldCheck, ShieldOff, ArrowRight, Inbox, ShoppingBag, Users as UsersIcon, AlertTriangle, Send,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import api, { readError } from "@/lib/api";

function Tile({ title, value, sub, icon: Icon, to, testid, accent = false, danger = false }) {
  const content = (
    <Card className="bg-card border-border/60 shadow-ambient hover:shadow-interactive transition-shadow h-full">
      <CardContent className="p-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{title}</div>
          <div className={`mt-2 font-serif text-3xl ${danger ? "text-rose-700" : accent ? "text-primary" : "text-foreground"}`}>{value}</div>
          {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
        </div>
        <div className={`rounded-lg p-2.5 ${danger ? "bg-rose-100" : "bg-primary/10"}`}>
          <Icon className={`h-5 w-5 ${danger ? "text-rose-700" : "text-primary"}`} strokeWidth={1.5} />
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} data-testid={testid} className="block">{content}</Link> : <div data-testid={testid}>{content}</div>;
}

function Spark({ data, color = "#C97B5C", testid }) {
  return (
    <div className="h-12" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <Line type="monotone" dataKey="count" stroke={color} strokeWidth={1.8} dot={false} isAnimationActive={false} />
          <Tooltip
            cursor={false}
            contentStyle={{ background: "#3D2418", border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 11 }}
            labelStyle={{ color: "#F5EDE0", fontSize: 10, marginBottom: 2 }}
            itemStyle={{ color: "#C97B5C" }}
            formatter={(v) => [v, "count"]}
            labelFormatter={(l) => l}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function fmtRel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    return `${days}d ago`;
  } catch { return iso; }
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/dashboard/full-stats")
      .then((r) => setData(r.data))
      .catch((err) => setError(readError(err, "Could not load stats.")));
  }, []);

  const k = data?.kpis || {};
  const recent = data?.recent_audit || [];
  const fmtEur = (v) => (typeof v === "number" ? new Intl.NumberFormat("hr-HR", { style: "currency", currency: "EUR" }).format(v) : "—");

  return (
    <div className="space-y-10 animate-fade-up" data-testid="admin-dashboard">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary">CMS · Workshop</div>
        <h1 className="mt-1 font-serif text-4xl text-foreground">Dobrodošli, {user?.email?.split("@")[0]}.</h1>
        <p className="mt-2 text-base text-muted-foreground max-w-2xl">Activity across the last 30 days. Sparkline data refreshes on page load.</p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive" data-testid="dashboard-error">{error}</div>
      )}

      {/* KPI grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Tile testid="tile-inquiries" title="New inquiries" value={k.inquiries_new ?? "—"} icon={Inbox} to="/admin/inquiries" accent={(k.inquiries_new ?? 0) > 0} />
        <Tile testid="tile-paid-orders" title="Paid orders · 30d" value={k.paid_orders_30d_count ?? "—"} sub={k.paid_orders_30d_amount_eur ? fmtEur(k.paid_orders_30d_amount_eur) : "—"} icon={ShoppingBag} to="/admin/orders" />
        <Tile testid="tile-messages" title="Unread messages" value={k.unread_messages ?? "—"} icon={Mail} to="/admin/messages" accent={(k.unread_messages ?? 0) > 0} />
        <Tile testid="tile-email-fails" title="Email fails · 7d" value={k.email_failures_7d ?? "—"} icon={Send} to="/admin/email-log?status=failed" danger={(k.email_failures_7d ?? 0) > 0} />
      </div>

      {/* Sparklines */}
      <div className="grid md:grid-cols-2 gap-5">
        <Card className="bg-card border-border/60 shadow-ambient" data-testid="spark-orders-card">
          <CardContent className="p-5">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Orders · last 30 days</div>
              <div className="font-serif text-lg text-foreground">{k.paid_orders_30d_count ?? "—"}</div>
            </div>
            <div className="mt-3"><Spark data={data?.sparklines?.orders_30d || []} testid="spark-orders" /></div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/60 shadow-ambient" data-testid="spark-inquiries-card">
          <CardContent className="p-5">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Inquiries · last 30 days</div>
              <div className="font-serif text-lg text-foreground">{(data?.sparklines?.inquiries_30d || []).reduce((s, d) => s + d.count, 0)}</div>
            </div>
            <div className="mt-3"><Spark data={data?.sparklines?.inquiries_30d || []} color="#8B5A3C" testid="spark-inquiries" /></div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Recent activity */}
        <Card className="bg-card border-border/60 shadow-ambient lg:col-span-2" data-testid="recent-activity-card">
          <CardContent className="p-6">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Recent activity</div>
              <Link to="/admin/audit-log" className="text-xs text-primary hover:underline">View all</Link>
            </div>
            {recent.length === 0 ? (
              <div className="mt-4 text-sm text-muted-foreground">No events yet.</div>
            ) : (
              <ul className="mt-4 divide-y divide-border/60">
                {recent.map((r) => (
                  <li key={r.id} className="py-2.5 flex items-baseline justify-between gap-3 text-sm">
                    <span className="flex items-center gap-2 min-w-0">
                      <Badge variant="outline" className="font-mono text-[10px] shrink-0">{r.action}</Badge>
                      <span className="text-muted-foreground truncate">{r.actor_email || "system"}</span>
                    </span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap" title={r.created_at}>{fmtRel(r.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Quick stats column */}
        <div className="space-y-5">
          <Card className="bg-card border-border/60 shadow-ambient">
            <CardContent className="p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Workshop catalog</div>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex items-baseline justify-between"><span className="text-muted-foreground">Products visible</span><span className="font-medium">{k.products_visible ?? "—"} <span className="text-xs text-muted-foreground">/ {k.products_total ?? "—"}</span></span></li>
                <li className="flex items-baseline justify-between"><span className="text-muted-foreground">Gallery items</span><span className="font-medium">{k.gallery_visible ?? "—"} <span className="text-xs text-muted-foreground">/ {k.gallery_total ?? "—"}</span></span></li>
                <li className="flex items-baseline justify-between"><span className="text-muted-foreground">CMS users</span><span className="font-medium">{k.users_total ?? "—"}</span></li>
                <li className="flex items-baseline justify-between"><span className="text-muted-foreground">Audit events</span><span className="font-medium">{k.audit_events_total ?? "—"}</span></li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/60 shadow-ambient">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Account security</div>
                  <div className="mt-2 font-serif text-xl text-foreground">{user?.is_2fa_enabled ? "2FA active" : "2FA off"}</div>
                </div>
                <div className={`rounded-lg p-2.5 ${user?.is_2fa_enabled ? "bg-primary/10" : "bg-muted"}`}>
                  {user?.is_2fa_enabled ? <ShieldCheck className="h-5 w-5 text-primary" strokeWidth={1.5} /> : <ShieldOff className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />}
                </div>
              </div>
              {!user?.is_2fa_enabled && (
                <Link to="/admin/2fa-setup">
                  <Button data-testid="dashboard-enable-2fa-btn" className="mt-4 h-9 w-full shadow-interactive">
                    Enable 2FA <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick actions */}
      <Card className="bg-card border-border/60 shadow-ambient">
        <CardContent className="p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Quick actions</div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <QuickAction to="/admin/pages" icon={FileText} title="Edit pages" testid="qa-pages" />
            <QuickAction to="/admin/products" icon={Package} title="Manage products" testid="qa-products" />
            <QuickAction to="/admin/gallery" icon={ImageIcon} title="Update gallery" testid="qa-gallery" />
            <QuickAction to="/admin/users" icon={UsersIcon} title="Manage users" testid="qa-users" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QuickAction({ to, icon: Icon, title, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-3 rounded-lg border border-border/60 px-4 py-3 hover:bg-muted/50 hover:border-primary/30 transition-colors">
      <Icon className="h-4 w-4 text-primary" strokeWidth={1.5} />
      <span className="text-sm text-foreground">{title}</span>
      <ArrowRight className="h-3.5 w-3.5 ml-auto text-muted-foreground" strokeWidth={1.5} />
    </Link>
  );
}
